import { isSequentialStopsReadModeEnabled } from './sequentialStopsReadFlags';

export type StopOrderKey = { position?: number | null; stopId?: number | null; id?: number | null };

export function sortStopsByPosition<T extends StopOrderKey>(stops: T[]): T[] {
  return [...stops].sort(
    (a, b) =>
      Number(a.position ?? 0) - Number(b.position ?? 0) ||
      Number(a.stopId ?? a.id ?? 0) - Number(b.stopId ?? b.id ?? 0)
  );
}

/** Legacy UI order: all loadings (by position), then all unloadings (by position). */
export function sortStopsLegacyTypeGrouped<T extends StopOrderKey & { type?: string | null }>(
  stops: T[]
): T[] {
  const loadings = stops
    .filter((s) => String(s.type || '').toLowerCase() === 'loading')
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
  const unloadings = stops
    .filter((s) => String(s.type || '').toLowerCase() === 'unloading')
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
  return [...loadings, ...unloadings];
}

export function resolveStopsReadOrder<T extends StopOrderKey & { type?: string | null }>(
  stops: T[]
): T[] {
  if (isSequentialStopsReadModeEnabled()) {
    return sortStopsByPosition(stops);
  }
  return sortStopsLegacyTypeGrouped(stops);
}

function orderSignature(stops: StopOrderKey[]): string {
  return stops
    .map((s) => String(s.stopId ?? s.id ?? s.position ?? '?'))
    .join('>');
}

export function logSequentialStopsReadShadow(params: {
  routeId?: number;
  context: string;
  stops: Array<StopOrderKey & { type?: string | null }>;
}): void {
  if (process.env.NODE_ENV === 'production') return;
  if (process.env.REACT_APP_SEQUENTIAL_STOPS_READ_SHADOW_DEBUG !== 'true') return;

  const byPosition = sortStopsByPosition(params.stops);
  const legacy = sortStopsLegacyTypeGrouped(params.stops);
  const posSig = orderSignature(byPosition);
  const legacySig = orderSignature(legacy);
  if (posSig === legacySig) return;

  // eslint-disable-next-line no-console
  console.debug('[SequentialStopsRead][shadow]', {
    routeId: params.routeId,
    context: params.context,
    positionOrder: posSig,
    legacyOrder: legacySig,
    policySource: 'position'
  });
}
